import React, { FC, useMemo } from 'react';

import { GREChecklistItem } from './gre/greCheckList';
import { ICertificate } from '../custom-general/custom-certificates/form';

import withPDF from '../../hoc/withPDF';

import AnomalyRecommendation from './anomaly-recommendation';
import Conditional from '../../common/conditional';
import CustomCertificatePdf from '../custom-general/custom-certificates/pdf';
import CustomPDFExport from '../../blocks/pdf-export';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import CustomDamageClass from '../custom-general/custom-damage-class';
import DamageClassSummary from './damage-class-summary';
import ExecutiveSummary from './executive-summary';
import GREChecklistReport from './greChecklistReport';
import OverviewSheet from '../custom-general/overview-sheet';
import PipingAnalytics from './report-items/pipingAnalytics';

import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface FVIPipingReportProps {
	certificates?: ICertificate;
	coverData: models.CustomFVIData;
	executiveSummary: string;
	reportTitle: string;
	greChecklistItems?: GREChecklistItem[];
	projectDetails: models.Project;
	includeSummary: models.IncludeSummary;
	pipingItems: models.FVIPipingItem[];
}

const FVIPipingReport: FC<FVIPipingReportProps> = (props) => {
	const { inspectionTypeName } = props.projectDetails;

	const referenceDocuments: models.CoverDataItem[] = useMemo(() => {
		return [
			{
				label: '1. Scope Of Work',
				value: props.coverData?.scopeOfWork,
			},
			{
				label: '2. VT Procedure.',
				value: props.coverData?.vtProcedure,
			},
			{
				label: '3. Rope Access Code of Practice.',
				value: props.coverData?.ropeAccessCodeOfPractice,
			},
			{
				label: '4. Work Procedure.',
				value: props.coverData?.exxonMobilWorkProcedure,
			},
		];
	}, [props.coverData]);

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

	const executiveSummary = useMemo(() => {
		if (props.includeSummary === 'include') {
			return props.executiveSummary + utils.getExecutiveSummaryAffix(props.pipingItems, 'pipe(s)');
		}

		return props.executiveSummary;
	}, [props.pipingItems, props.includeSummary, props.executiveSummary]);

	const overview = useCoverSheetOverview();
	const inspectionTypeLabel = utils.getExtractedInspectionName(inspectionTypeName);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={inspectionTypeLabel} />

			<CustomInfoSheet
				projectDetails={props.projectDetails}
				workOrderNumber={props.coverData.workOrderNumber}
				reportTitle={props.reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			<Conditional isVisible={!props.greChecklistItems}>
				<CustomDamageClass />
			</Conditional>

			<ExecutiveSummary summary={executiveSummary} />

			{props.certificates && <CustomCertificatePdf certificates={props.certificates} />}

			<Conditional isVisible={!props.greChecklistItems}>
				<DamageClassSummary
					items={props.pipingItems}
					columns={damageClassColumns}
					itemNumberKey='lineNumber'
				/>

				<CustomPDFExport>
					{(params) => (
						<AnomalyRecommendation 
							projectDetails={props.projectDetails} 
							items={props.pipingItems}
							exportParams={params}
						/>
					)}
				</CustomPDFExport>
			</Conditional>

			{props.greChecklistItems && <GREChecklistReport items={props.greChecklistItems!} />}

			<PipingAnalytics includeEmptyPDONotice includePDO items={props.pipingItems} useGroupedTitle />
		</React.Fragment>
	);
};

const damageClassColumns: models.Column[] = [
	{
		path: 'lineNumber',
		label: 'Line Number',
	},
];

export default withPDF(FVIPipingReport);
